import { Button, Form, Input, Select, Switch, TimePicker, message } from "antd";
import {
  useFetchAgencyQuery,
  useUpdateAgencyMutation,
} from "features/basicInfo/agencyPortal/agencySettingsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useCallback, useEffect } from "react";
import styled from "styled-components";
import moment from "moment-timezone";

const StyledForm = styled(Form)`
  width: 600px;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;
`;

const StyledTimePickerItem = styled(Form.Item)`
  .ant-form-item-label {
    text-align: left;
  }
`;

export default function BasicInfo() {
  const [form] = Form.useForm();
  const [updateAgency, { isLoading: isUpdateLoading }] = useUpdateAgencyMutation();
  const { data: agencySettings, isLoading: isAgencySettingsLoading } = useFetchAgencyQuery();
  const { data: constants, isLoading: isConstantsLoading } = useFetchBackendConstantsQuery();
  const {
    data: { [PERMISSIONS.AGENCY__UPDATE]: isUpdateAgencyAuthorized },
  } = useAuthorizations();

  useEffect(() => {
    if (agencySettings) {
      const formValues = {
        ...agencySettings,
        permissionToContactStartTime: agencySettings.permissionToContactStartTime
          ? moment(agencySettings.permissionToContactStartTime, "HH:mm:ss")
          : null,
        permissionToContactEndTime: agencySettings.permissionToContactEndTime
          ? moment(agencySettings.permissionToContactEndTime, "HH:mm:ss")
          : null,
      };

      form.setFieldsValue(formValues);
    }
  }, [agencySettings, form]);

  const onSave = useCallback(async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      accountPaymentStrategy: values.accountPaymentStrategy ?? null,
      permissionToContactStartTime: values.permissionToContactStartTime
        ? values.permissionToContactStartTime.format("HH:mm:ss")
        : null,
      permissionToContactEndTime: values.permissionToContactEndTime
        ? values.permissionToContactEndTime.format("HH:mm:ss")
        : null,
    };
    const result = await updateAgency(payload);
    if ("data" in result) {
      message.success("Agency settings updated successfully");
    }
  }, [form, updateAgency]);

  const timezones = [
    { display: "UTC", value: "UTC" },
    { display: "Eastern Standard Time (EST)", value: "US/Eastern" },
    { display: "Central Standard Time (CST)", value: "US/Central" },
    { display: "Mountain Standard Time (MST)", value: "US/Mountain" },
    { display: "Pacific Standard Time (PST)", value: "US/Pacific" },
    { display: "Alaska Standard Time (AKST)", value: "US/Alaska" },
    { display: "Hawaii-Aleutian Standard Time (HST)", value: "US/Hawaii" },
    { display: "Atlantic Standard Time (AST)", value: "America/Halifax" },
    { display: "Newfoundland Standard Time (NST)", value: "America/St_Johns" },
  ];

  return (
    <StyledForm form={form} onFinish={onSave} disabled={!isUpdateAgencyAuthorized}>
      <h4>Agency Name</h4>
      <Form.Item name="name">
        <Input placeholder="Enter agency name" />
      </Form.Item>

      <h4>Letter Codes</h4>
      <Form.Item name="letterCodes">
        <Select
          loading={isAgencySettingsLoading || isConstantsLoading || isUpdateLoading}
          mode="tags"
          popupMatchSelectWidth={false}
          options={agencySettings?.letterCodes?.map(({ value }) => ({
            label: value,
            value,
          }))}
        />
      </Form.Item>
      <h4>Timezone</h4>
      <Form.Item name="timezone">
        <Select
          showSearch
          filterOption={(input, option) =>
            (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
          }
          loading={isAgencySettingsLoading || isConstantsLoading || isUpdateLoading}
          popupMatchSelectWidth={false}
          options={timezones?.map(({ display, value }) => ({
            label: display,
            value,
          }))}
        />
      </Form.Item>
      <h4>Account Payment Strategy</h4>
      <Form.Item
        name="accountPaymentStrategy"
        rules={[
          {
            required: true,
            message: "Select an account payment strategy",
          },
        ]}
      >
        <Select
          allowClear
          maxTagTextLength={20}
          loading={isAgencySettingsLoading || isConstantsLoading || isUpdateLoading}
          popupMatchSelectWidth={false}
          options={constants?.accountPaymentStrategies?.map(({ display, value }) => ({
            label: display,
            value,
          }))}
        />
      </Form.Item>
      <h4>Permission to contact time window</h4>

      <StyledTimePickerItem
        label="Start time:"
        name="permissionToContactStartTime"
        labelCol={{ span: 3 }}
        labelAlign="left"
      >
        <TimePicker format="HH:mm" placeholder="Select start time" />
      </StyledTimePickerItem>

      <StyledTimePickerItem
        label="End time:"
        name="permissionToContactEndTime"
        labelCol={{ span: 3 }}
        labelAlign="left"
      >
        <TimePicker format="HH:mm" placeholder="Select end time" />
      </StyledTimePickerItem>

      <h4>Collector Continuity</h4>
      <Form.Item name="isCollectorContinuityEnabled">
        <Switch
          defaultChecked={!!agencySettings?.isRequired}
          checkedChildren="Enabled"
          unCheckedChildren="Disabled"
        />
      </Form.Item>
      {isUpdateAgencyAuthorized && (
        <StyledButton type="primary" onClick={form.submit} loading={isUpdateLoading}>
          Save
        </StyledButton>
      )}
    </StyledForm>
  );
}
